import React from 'react';
import { useScrollAnimation } from '../hooks/useScrollAnimation';
import { Monitor, ShoppingBag, UtensilsCrossed, GraduationCap, Cpu, Briefcase, Heart, Wrench } from 'lucide-react';

const classes = [
  { icon: Monitor, classNum: '09', name: 'Technology & Electronics', examples: 'Software, apps, electronics, headphones, chargers', color: 'bg-[#7C3AED]' },
  { icon: ShoppingBag, classNum: '25', name: 'Clothing & Apparel', examples: 'T-shirts, shoes, hats, athletic wear, accessories', color: 'bg-[#14B8A6]' },
  { icon: Briefcase, classNum: '35', name: 'Business Services', examples: 'Advertising, consulting, retail, online stores', color: 'bg-violet-600' },
  { icon: UtensilsCrossed, classNum: '43', name: 'Food & Restaurant', examples: 'Restaurants, catering, cafes, food trucks', color: 'bg-emerald-600' },
  { icon: GraduationCap, classNum: '41', name: 'Education & Training', examples: 'Online courses, coaching, seminars, publications', color: 'bg-[#7C3AED]' },
  { icon: Cpu, classNum: '42', name: 'Software & IT', examples: 'SaaS platforms, web hosting, cloud services', color: 'bg-[#14B8A6]' },
  { icon: Heart, classNum: '44', name: 'Health & Beauty', examples: 'Skincare, supplements, salon services, wellness', color: 'bg-violet-600' },
  { icon: Wrench, classNum: '37', name: 'Construction & Repair', examples: 'Building services, plumbing, HVAC, installation', color: 'bg-emerald-600' },
];

const ClassCard = ({ cls, index }) => {
  const card = useScrollAnimation({ threshold: 0.1 });
  const Icon = cls.icon;

  return (
    <div
      ref={card.ref}
      data-testid={`tm-class-${index}`}
      className={`group bg-white rounded-2xl border border-zinc-100 p-6 transition-all duration-500 hover:shadow-[0_20px_50px_rgba(124,58,237,0.15)] hover:-translate-y-2 scroll-fade-up scroll-delay-${(index % 4) + 1} ${card.isVisible ? 'scroll-visible' : ''}`}
    >
      <div className="flex items-start gap-4">
        <div className={`w-11 h-11 rounded-xl ${cls.color} flex items-center justify-center flex-shrink-0`}>
          <Icon className="w-5 h-5 text-white" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <span className="text-[10px] font-bold text-zinc-400 tracking-wider">CLASS {cls.classNum}</span>
          </div>
          <h4 className="text-sm font-bold text-zinc-900 mb-1" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>
            {cls.name}
          </h4>
          <p className="text-xs text-zinc-400 leading-relaxed">{cls.examples}</p>
        </div>
      </div>
    </div>
  );
};

const TrademarkClassesSection = () => {
  const header = useScrollAnimation({ threshold: 0.15 });

  return (
    <section data-testid="trademark-classes-section" className="py-24 md:py-32 bg-white relative overflow-hidden">
      <div className="absolute bottom-0 left-0 w-[400px] h-[400px] rounded-full bg-[#14B8A6]/3 blur-[120px]" />

      <div className="mx-auto w-full max-w-7xl px-4 md:px-8 relative z-10">
        <div ref={header.ref} className={`text-center max-w-2xl mx-auto mb-16 scroll-fade-up ${header.isVisible ? 'scroll-visible' : ''}`}>
          <span className="inline-block bg-[#14B8A6]/10 text-[#14B8A6] px-4 py-1.5 rounded-full text-xs font-bold tracking-wide mb-4">
            TRADEMARK CLASSES
          </span>
          <h2 className="text-4xl sm:text-5xl font-black tracking-tight text-zinc-900 mb-4" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>
            Popular USPTO Classification
          </h2>
          <p className="text-base text-zinc-500 leading-relaxed">
            The USPTO uses 45 classes to categorize goods and services. Here are the most common classes our clients register their trademarks in.
          </p>
        </div>

        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {classes.map((cls, i) => (
            <ClassCard key={i} cls={cls} index={i} />
          ))}
        </div>

        <div className="text-center mt-10">
          <p className="text-sm text-zinc-400">
            Not sure which class applies to your business? Our trademark attorneys will identify the correct classification during your consultation.
          </p>
        </div>
      </div>
    </section>
  );
};

export default TrademarkClassesSection;
